/**
 * Loose ZEC address-format validators.
 *
 * ZcashWallet is Zcash-only. Real validation (checksums, bech32m, ZIP-316
 * parsing for Unified Addresses) happens deeper in the send pipeline —
 * this catches obvious typos before kicking off a quote or deposit
 * that would fail anyway. Phase 8 (MAYA swap) will use this to validate
 * the recipient chain when sending out to other networks via MAYA, so
 * the non-ZEC validators come back then.
 */

const ZEC_TRANSPARENT = /^t[13][a-km-zA-HJ-NP-Z1-9]{20,}$/;
const ZEC_SAPLING_LEGACY = /^zs1[0-9a-z]{60,}$/i;
const ZEC_UNIFIED = /^u1[0-9a-z]{50,}$/i;

/**
 * Classify a pasted Zcash address. UI uses the result to flip
 * "Private send" vs "Public deshielding" badges; the send pipeline
 * uses it to decide between transparent and shielded output types.
 */
export type ZecAddressKind =
  | "transparent" // t1.../t3...
  | "sapling-legacy" // zs1...
  | "unified" // u1... (ZIP-316; Sapling and/or Orchard receivers)
  | "invalid";

export const classifyZecAddress = (addr: string): ZecAddressKind => {
  const a = addr.trim();
  if (ZEC_UNIFIED.test(a)) return "unified";
  if (ZEC_SAPLING_LEGACY.test(a)) return "sapling-legacy";
  if (ZEC_TRANSPARENT.test(a)) return "transparent";
  return "invalid";
};

export const isValidZecAddress = (addr: string): boolean =>
  classifyZecAddress(addr) !== "invalid";
